import { useEffect, useState } from 'react'

import { leerNumero } from '../formato'
import type { Opciones } from '../tipos'

interface Props {
  /** Los costos cambiados a mano: la instalacion por kWp vive en `costos.instalacion`. */
  costos: Record<string, number>
  alCambiar: (costos: Record<string, number>) => void
  tarifas?: Opciones['instalacion_usd_kwp']
  conBateria: boolean
  /** Con el total de la instalacion fijado, o la partida quitada, la tarifa por kWp no cuenta. */
  bloqueado: boolean
}

/**
 * El costo de la instalacion POR kWp. Sin escribir nada, el motor aplica el suyo (130 on-grid,
 * 170 con bateria): aqui se muestra y se puede cambiar. Es la misma cifra que la celda de costo
 * de la tabla, por eso se lee y se escribe en `costos.instalacion` y no hay un segundo estado.
 */
export function CampoInstalacionKwp({ costos, alCambiar, tarifas, conBateria, bloqueado }: Props) {
  const costo = costos.instalacion
  const [texto, setTexto] = useState(costo === undefined ? '' : String(costo))
  const [invalido, setInvalido] = useState(false)

  // La celda de la tabla (o su "volver al catalogo") cambia el mismo valor: el campo lo sigue.
  useEffect(() => {
    if (leerNumero(texto) !== (costo ?? null)) {
      setTexto(costo === undefined ? '' : String(costo))
      setInvalido(false)
    }
    // Solo cuando cambia el costo de fuera; reaccionar al texto pisaria lo que se esta escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costo])

  const automatico = tarifas ? tarifas[conBateria ? 'con_bateria' : 'on_grid'] : undefined

  const escribir = (valor: string) => {
    setTexto(valor)
    const n = leerNumero(valor)
    const malo = Number.isNaN(n) || (n !== null && n < 0)
    setInvalido(malo)
    const { instalacion: _quitado, ...resto } = costos
    alCambiar(n === null || malo ? resto : { ...resto, instalacion: n })
  }

  return (
    <label>
      Instalación por kWp (USD)
      <input
        value={bloqueado ? '' : texto}
        onChange={(e) => escribir(e.target.value)}
        inputMode="decimal"
        disabled={bloqueado}
        aria-invalid={invalido}
        placeholder={bloqueado ? 'No aplica' : automatico === undefined ? 'Vacío = automático' : `${automatico} (automático)`}
      />
      <small className="ayuda">
        {invalido
          ? 'Escribe un valor en USD, por ejemplo 130. Mientras no sea válido se usa el automático.'
          : bloqueado
            ? 'Hay un total de instalación fijado (o la partida está quitada): la tarifa por kWp no se usa.'
            : tarifas === undefined
              ? 'Es tu costo por kWp; se le suma la utilidad y 15 % de IVA.'
              : `Automático: ${tarifas.on_grid} USD/kWp en on-grid (0,13 por Wp) y ${tarifas.con_bateria} con batería (0,17 por Wp). Es tu costo: se le suma la utilidad y 15 % de IVA. Escribe otro valor para cambiarlo.`}
      </small>
    </label>
  )
}
